import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocPortal, MapLocKeys } from '@cm/core/localization';
import { BadgeDetailViewLocs } from 'libs/cp/ui/src/lib/components/badge-detail-view/badge-detail-view.locs';

const dpRoot = 'Share|Badges';

export type CPBadgeDetailPageLocKeys =
	| 'CPBadgeDetailBackButton'
	| 'CPBadgeDetailShareButton'
	| 'CPBadgeDetailPageTitle';

const sharedLocDefs: Pick<LocDef, 'feature' | 'htmlAllowed' | 'shared' | 'portal'> = {
	feature: HubFeature.ShareCertMetricsBadging,
	htmlAllowed: false,
	shared: false,
	portal: 'CP' as LocPortal,
};

export const cpBadgeDetailPageLocs: LocDefs<CPBadgeDetailPageLocKeys> = {
	CPBadgeDetailBackButton: {
		legacyName: 'Share.CertMetricsBadging/Share.Badges.Button.Back',
		defaultValue: 'Go back',
		description: 'The back button in the top right of the badge details screen',
		displayPath: `${dpRoot}|Back Button`,
		displayType: 'Button',
		...sharedLocDefs,
	},
	CPBadgeDetailShareButton: {
		legacyName: 'Share.CertMetricsBadging/Share.Badges.Button.Share',
		defaultValue: 'Share',
		description: 'Share menu button',
		displayPath: `${dpRoot}|Share Button`,
		displayType: 'Button',
		...sharedLocDefs,
	},
	CPBadgeDetailPageTitle: {
		legacyName: 'Share.CertMetricsBadging/Share.Badges.Title.PageTitle',
		defaultValue: 'Badges',
		description: 'Page and tab title for the share badges page.',
		displayPath: `${dpRoot}|Page Title`,
		displayType: 'Title',
		...sharedLocDefs,
	},
};

export type BadgeDetailLocKeys =
	| 'CPBadgeDetailContentImageAltTag'
	| 'CPBadgeDetailContentVisit'
	| 'CPBadgeDetailLabelOrganization'
	| 'CPBadgeDetailLabelUrl'
	| 'CPBadgeDetailTitleBadgeDetails'
	| 'CPBadgeDetailLabelIssuedTo'
	| 'CPBadgeDetailLabelIssuer'
	| 'CPBadgeDetailLabelViews'
	| 'CPBadgeDetailLabelDateEarned'
	| 'CPBadgeDetailLabelLastAdjustmentDate'
	| 'CPBadgeDetailLabelValidThrough'
	| 'CPBadgeDetailLabelCriteria'
	| 'CPBadgeDetailLabelDescription';

export const cpBadgeDetailLocs: LocDefs<BadgeDetailLocKeys> = {
	CPBadgeDetailContentImageAltTag: {
		legacyName: 'Share.CertMetricsBadging/Public.Badge.ImageInfo.BadgeAltTag',
		defaultValue: 'Badge logo',
		description: 'Badge image text used for accessibility',
		displayPath: `${dpRoot}|Issuer Details`,
		displayType: 'Content',
		...sharedLocDefs,
	},
	CPBadgeDetailLabelDescription: {
		legacyName: 'Share.CertMetricsBadging/Public.Badge.FieldLabel.Description',
		defaultValue: 'Description',
		description: 'Label for the description field',
		displayPath: `${dpRoot}|Description`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPBadgeDetailLabelDateEarned: {
		legacyName: 'Share.CertMetricsBadging/Public.Badge.FieldLabel.DateEarned',
		defaultValue: 'Date Earned',
		description: 'Label for the active date field',
		displayPath: `${dpRoot}|Date Earned`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPBadgeDetailLabelIssuedTo: {
		legacyName: 'Share.CertMetricsBadging/Public.Badge.FieldLabel.IssuedTo',
		defaultValue: 'Issued To',
		description: 'Label for the issued to field',
		displayPath: `${dpRoot}|Issued To`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPBadgeDetailLabelIssuer: {
		legacyName: 'Share.CertMetricsBadging/Public.Badge.FieldLabel.Issuer',
		defaultValue: 'Issuer Details',
		description: 'Label for the issuers field',
		displayPath: `${dpRoot}|Issuer`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPBadgeDetailLabelLastAdjustmentDate: {
		legacyName: 'Share.CertMetricsBadging/Public.Badge.FieldLabel.LastAdjustmentDate',
		defaultValue: 'Last Extension Date',
		description: 'Label for the last adjustment date',
		displayPath: `${dpRoot}|Last Extension Date`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPBadgeDetailLabelValidThrough: {
		legacyName: 'Share.CertMetricsBadging/Public.Badge.FieldLabel.ValidThrough',
		defaultValue: 'Valid Through',
		description: 'Label for the valid through field',
		displayPath: `${dpRoot}|Valid Through`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPBadgeDetailTitleBadgeDetails: {
		legacyName: 'Share.CertMetricsBadging/Public.Badge.Title.PageTitle',
		defaultValue: 'Badge',
		description: 'Page title for the badge sharing page.',
		displayPath: `${dpRoot}|Page Title`,
		displayType: 'Title',
		...sharedLocDefs,
	},
	CPBadgeDetailLabelOrganization: {
		legacyName: 'Share.CertMetricsBadging/Share.Badges.Details.FieldLabel.Organization',
		defaultValue: 'Issuer',
		description: 'The field label for the issuing organization',
		displayPath: `${dpRoot}|Organization`,
		displayType: 'Title',
		...sharedLocDefs,
	},
	CPBadgeDetailLabelUrl: {
		legacyName: 'Share.CertMetricsBadging/Share.Badges.Details.FieldLabel.Url',
		defaultValue: 'URL',
		description: 'The field label for the url to the issuing organization',
		displayPath: `${dpRoot}|URL`,
		displayType: 'Title',
		...sharedLocDefs,
	},
	CPBadgeDetailContentVisit: {
		defaultValue: 'Visit',
		description: 'Content shown for visiting a different page',
		displayPath: `${dpRoot}|Visit`,
		displayType: 'Content',
		...sharedLocDefs,
	},
	CPBadgeDetailLabelViews: {
		legacyName: 'Share.CertMetricsBadging/Share.Badges.Details.FieldLabel.ViewCount',
		defaultValue: 'Views',
		displayPath: `${dpRoot}|Views`,
		description: 'The field label for the number of badge views',
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPBadgeDetailLabelCriteria: {
		legacyName: 'Share.CertMetricsBadging/Share.Badges.Details.FieldLabel.Criteria',
		defaultValue: 'Criteria',
		displayPath: `${dpRoot}|Criteria`,
		description:
			'The field label for the criteria url. This url will link to the page that provides the criteria for obtaining the badge',
		displayType: 'Label',
		...sharedLocDefs,
	},
};
export const BADGE_DETAIL_LOC_KEYS = MapLocKeys<BadgeDetailLocKeys>(cpBadgeDetailLocs);
export const LOC_KEYS = MapLocKeys<CPBadgeDetailPageLocKeys>(cpBadgeDetailPageLocs);

export const mapBadgeDetailLocs: BadgeDetailViewLocs = {
	BadgeDetailViewContentImageAltTag: BADGE_DETAIL_LOC_KEYS.CPBadgeDetailContentImageAltTag,
	BadgeDetailViewContentVisit: BADGE_DETAIL_LOC_KEYS.CPBadgeDetailContentVisit,
	BadgeDetailViewLabelOrganization: BADGE_DETAIL_LOC_KEYS.CPBadgeDetailLabelOrganization,
	BadgeDetailViewLabelUrl: BADGE_DETAIL_LOC_KEYS.CPBadgeDetailLabelUrl,
	BadgeDetailViewTitleBadgeDetails: BADGE_DETAIL_LOC_KEYS.CPBadgeDetailTitleBadgeDetails,
	BadgeDetailViewLabelIssuedTo: BADGE_DETAIL_LOC_KEYS.CPBadgeDetailLabelIssuedTo,
	BadgeDetailViewLabelIssuer: BADGE_DETAIL_LOC_KEYS.CPBadgeDetailLabelIssuer,
	BadgeDetailViewLabelViews: BADGE_DETAIL_LOC_KEYS.CPBadgeDetailLabelViews,
	BadgeDetailViewLabelDateEarned: BADGE_DETAIL_LOC_KEYS.CPBadgeDetailLabelDateEarned,
	BadgeDetailViewLabelLastAdjustmentDate:
		BADGE_DETAIL_LOC_KEYS.CPBadgeDetailLabelLastAdjustmentDate,
	BadgeDetailViewLabelValidThrough: BADGE_DETAIL_LOC_KEYS.CPBadgeDetailLabelValidThrough,
	BadgeDetailViewLabelCriteria: BADGE_DETAIL_LOC_KEYS.CPBadgeDetailLabelCriteria,
	BadgeDetailViewLabelDescription: BADGE_DETAIL_LOC_KEYS.CPBadgeDetailLabelDescription,
};
